import fs from 'fs-extra';
import { getLicenseCachePath, getSmartStackDir } from './license-paths.js';

/** Normalized feature scope — shared between cache, client, and license */
export interface LicenseFeatureScope {
  applicationId: string | null;
  applicationCode: string;
  modules: Array<{ moduleId: string; moduleCode: string }>;
}

export interface CachedValidationResult {
  valid: boolean;
  revoked: boolean;
  environment: string;
  features: LicenseFeatureScope[];
  maxInstances: number;
  maxUsers: number;
  expiresAt: string;
  gracePeriodDays: number;
  isPerpetual: boolean;
  renewalDate: string | null;
}

export interface CachedActivation {
  activated: boolean;
  activatedAt: string;
  machineFingerprint: string;
  instanceId: string;
  heartbeatIntervalHours: number;
  gracePeriodDays: number;
}

export interface CachedSigningKey {
  pem: string;
  fetchedAt: string;
}

export interface LicenseCache {
  version: 1;
  lastValidation: string | null;
  lastHeartbeat: string | null;
  lastValidationResult: CachedValidationResult | null;
  activation: CachedActivation | null;
  signingKey: CachedSigningKey | null;
}

function createEmptyCache(): LicenseCache {
  return {
    version: 1,
    lastValidation: null,
    lastHeartbeat: null,
    lastValidationResult: null,
    activation: null,
    signingKey: null,
  };
}

/**
 * Load the license cache from disk. Returns empty cache if missing or corrupted.
 */
export async function loadCache(): Promise<LicenseCache> {
  const cachePath = getLicenseCachePath();

  if (!(await fs.pathExists(cachePath))) {
    return createEmptyCache();
  }

  try {
    const data = await fs.readJSON(cachePath);
    if (data?.version === 1) return data as LicenseCache;
    return createEmptyCache();
  } catch {
    return createEmptyCache();
  }
}

/**
 * Save the license cache to disk.
 */
export async function saveCache(cache: LicenseCache): Promise<void> {
  await fs.ensureDir(getSmartStackDir());
  await fs.writeJSON(getLicenseCachePath(), cache, { spaces: 2 });
}

/**
 * Clear the license cache (delete file).
 */
export async function clearCache(): Promise<void> {
  const cachePath = getLicenseCachePath();
  if (await fs.pathExists(cachePath)) {
    await fs.remove(cachePath);
  }
}

/**
 * Check if the cached validation result is still fresh.
 */
export function isCacheValid(cache: LicenseCache, maxAgeMinutes: number = 60): boolean {
  if (!cache.lastValidation || !cache.lastValidationResult) return false;

  const lastValidation = new Date(cache.lastValidation);
  const ageMs = Date.now() - lastValidation.getTime();
  return ageMs < maxAgeMinutes * 60 * 1000;
}

/**
 * Check if a heartbeat is due based on the cache activation interval.
 */
export function isHeartbeatDue(cache: LicenseCache): boolean {
  if (!cache.activation) return false;
  if (!cache.lastHeartbeat) return true;

  const lastHeartbeat = new Date(cache.lastHeartbeat);
  const intervalMs = (cache.activation.heartbeatIntervalHours || 168) * 60 * 60 * 1000;
  return Date.now() - lastHeartbeat.getTime() >= intervalMs;
}

/**
 * Check if the license is currently in its grace period.
 */
export function isInGracePeriod(
  expiresAt: string,
  gracePeriodDays: number,
): { inGrace: boolean; daysLeft: number } {
  const expiry = new Date(expiresAt);
  const now = Date.now();
  const graceEndMs = expiry.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000;

  if (now <= expiry.getTime()) {
    // Not yet expired
    return { inGrace: false, daysLeft: gracePeriodDays };
  }

  if (now <= graceEndMs) {
    const daysLeft = Math.ceil((graceEndMs - now) / (24 * 60 * 60 * 1000));
    return { inGrace: true, daysLeft };
  }

  return { inGrace: false, daysLeft: 0 };
}

/**
 * Check if the grace period has expired (license should be blocked).
 */
export function isGraceExpired(expiresAt: string, gracePeriodDays: number): boolean {
  const expiry = new Date(expiresAt);
  const graceEndMs = expiry.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000;
  return Date.now() > graceEndMs;
}

/**
 * Check if the cached signing key is still fresh (TTL 24h).
 */
export function isSigningKeyCacheValid(cache: LicenseCache): boolean {
  if (!cache.signingKey?.pem || !cache.signingKey?.fetchedAt) return false;

  const fetchedAt = new Date(cache.signingKey.fetchedAt);
  const ageMs = Date.now() - fetchedAt.getTime();
  return ageMs < 24 * 60 * 60 * 1000; // 24 hours
}
